import { Globe, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Combobox,
  ComboboxLabel,
  ComboboxOption
} from '@components/atoms/ui-kit/combobox.tsx';
import { Language } from '@utils/types/shared/language.ts';

type Props = {
  value: string;
  languages: Language[];
  disabled: boolean;
  required: boolean;
  onChange: (key: string) => void;
};

export const RealtimeLanguageSelect = ({
  value,
  languages,
  disabled,
  required,
  onChange
}: Props) => {
  const { t } = useTranslation();
  const selected =
    languages.find(language => language.key === value) ||
    (value
      ? {
          key: value,
          name: value,
          is_private: false,
          bcp47: value,
          id: 0,
          is_trainable: false
        }
      : null);
  return (
    <Combobox<Language>
      aria-label={t('language')}
      disabled={disabled}
      required={required}
      value={selected}
      onChange={language => onChange(language?.key || '')}
      options={languages}
      displayValue={language =>
        language ? `${language.name} (${language.key})` : ''
      }
      filter={(language, query) => {
        const term = query.toLowerCase();
        return (
          language.name.toLowerCase().includes(term) ||
          language.key.toLowerCase().includes(term)
        );
      }}
      placeholder={t('select-language')}
      className="h-10 rounded-lg px-2.5 text-[13px] font-normal"
    >
      {language => (
        <ComboboxOption key={language.key} value={language}>
          {language.is_private ? (
            <Lock
              data-slot="icon"
              aria-hidden="true"
              className="text-orange-500"
            />
          ) : (
            <Globe
              data-slot="icon"
              aria-hidden="true"
              className="text-green-500"
            />
          )}
          <ComboboxLabel>{`${language.name} (${language.key})`}</ComboboxLabel>
          <span className="sr-only">
            {t(language.is_private ? 'private' : 'public')}
          </span>
        </ComboboxOption>
      )}
    </Combobox>
  );
};
